// services/nav-tab.service.ts
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { Tab } from '../shared/interfaces/tab.interface';
import { ComponentMap, COMPONENT_MAP_TOKEN } from '../shared/tokens/component-map.token';
import { NAVIGATION_DATA, NavItem } from '../layouts/navigation/navigation-data';

@Injectable({ providedIn: 'root' })
export class NavTabService {
  private tabs: Tab[] = [];
  private tabsSubject = new BehaviorSubject<Tab[]>([]);
  tabs$ = this.tabsSubject.asObservable();

  private selectedTabIndex = 0;
  private selectedTabSubject = new BehaviorSubject<number>(0);
  selectedTab$ = this.selectedTabSubject.asObservable();



  constructor(
    @Inject(COMPONENT_MAP_TOKEN) private componentMap: ComponentMap,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    // Only touch localStorage in the browser
    if (isPlatformBrowser(this.platformId)) {
      this.restoreTabsFromStorage();
    } else {
      // SSR: at least push a placeholder Dashboard so templates can render
      this.tabs = [{
        title: 'Dashboard',
        icon: 'dashboard',
        componentKey: 'dashboard',
        component: undefined, // resolved on the client
        path: 'dashboard1'
      }];
      this.tabsSubject.next([...this.tabs]);
      this.selectedTabSubject.next(0);
    }
  }

  consumeActiveTabData<T = any>(): T | null {
    const tab = this.getActiveTab();
    if (!tab || !tab.data) return null;

    const data = tab.data as T;

    // ✅ consume once
    tab.data = null;
    this.saveTabsToStorage();

    return data;
  }

  getTabs(): Tab[] {
    return this.tabs;
  }

  openScreen(tab: Omit<Tab, 'path' | 'component'> & {componentKey:string}, another=false) {
    const index=this.tabs.findIndex(t=>t.componentKey===tab.componentKey.toLowerCase().trim());
    if(!another && index>=0) this.setSelectedTab(index); else this.addTab(tab);
  }

  addTab(tab: Omit<Tab, 'path' | 'component'> & { componentKey: string }) {
    const basePath = (tab.title ?? 'tab').replace(/\s+/g, '').toLowerCase();
    const instanceNumber = this.tabs.filter(t => t.title === tab.title).length + 1;
    let suffix=instanceNumber;
    while(this.tabs.some(t=>t.path===`${basePath}${suffix}`)) suffix++;
    const newPath = `${basePath}${suffix}`;

    const cleanKey = tab.componentKey ? tab.componentKey.toLowerCase().trim() : '';
    const resolvedComponent = cleanKey ? this.componentMap[cleanKey] : undefined;

    if (!resolvedComponent) {
      console.warn('Component not found for key:', cleanKey);
    }

    const newTab: Tab = {
      ...tab,
      componentKey: cleanKey,
      component: resolvedComponent,
      path: newPath,
      data: tab.data ?? null,
    };

    this.tabs.push(newTab);
    this.tabsSubject.next([...this.tabs]);
    this.setSelectedTab(this.tabs.length - 1);
    this.saveTabsToStorage();
  }

  removeTab(index: number) {
    if (index <= 0 || index >= this.tabs.length) return;
    const active=this.tabs[this.selectedTabIndex];
    this.tabs.splice(index, 1);
    const newIndex = this.tabs.includes(active)?this.tabs.indexOf(active):Math.max(0,index-1);
    this.tabsSubject.next([...this.tabs]);
    this.setSelectedTab(newIndex);
    this.saveTabsToStorage();
  }

  setSelectedTab(index: number) {
    this.selectedTabIndex = index;
    this.selectedTabSubject.next(index);
    this.saveTabsToStorage();
  }

  private restoreTabsFromStorage() {
    try {
      const storedTabs = localStorage.getItem('openTabs');
      const storedIndex = localStorage.getItem('selectedTabIndex');

      if (storedTabs) {
        const parsedTabs = JSON.parse(storedTabs);
        this.tabs = parsedTabs.map((t: any) => {
          const oldKey = t.componentKey ? t.componentKey.toLowerCase().trim() : '';
          const cleanKey = oldKey==='customers' ? 'customermanagement' : oldKey;
          const resolved = cleanKey ? this.componentMap[cleanKey] : undefined;

          if (!resolved) console.warn('Component not found for key:', cleanKey);

          return {
            ...t,
            title: oldKey==='customers' ? 'Customer Management' : t.title,
            icon: oldKey==='customers' ? 'contacts' : t.icon,
            componentKey: cleanKey,
            component: resolved,
            data: t.data ?? null
          } as Tab;
        });
      }

      if (this.tabs.length === 0) {
        this.tabs.push({
          title: 'Dashboard',
          icon: 'dashboard',
          componentKey: 'dashboard',
          component: this.componentMap['dashboard'],
          path: 'dashboard1'
        });
      }

      const selected = parseInt(storedIndex || '0', 10);
      this.selectedTabIndex = selected < this.tabs.length ? selected : 0;
    } catch {
      // fallback
      this.tabs = [{
        title: 'Dashboard',
        icon: 'dashboard',
        componentKey: 'dashboard',
        component: this.componentMap['dashboard'],
        path: 'dashboard1'
      }];
      this.selectedTabIndex = 0;
    }

    this.tabsSubject.next([...this.tabs]);
    this.selectedTabSubject.next(this.selectedTabIndex);
  }

  private saveTabsToStorage() {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      const tabsToStore = this.tabs.map(t => ({ ...t, component: undefined }));
      localStorage.setItem('openTabs', JSON.stringify(tabsToStore));
      localStorage.setItem('selectedTabIndex', this.selectedTabIndex.toString());
    } catch {
      // ignore
    }
  }

  getNavItemFromTitle(title: string): NavItem | undefined {
    return this.findItem(NAVIGATION_DATA, item => item.title.toLowerCase().trim() === title.toLowerCase().trim());
  }

  getActiveTab(): Tab | null {
    return this.tabs[this.selectedTabIndex] ?? null;
  }


  private findItem(items: NavItem[], matcher: (item: NavItem) => boolean): NavItem | undefined {
    for (const item of items) {
      if (matcher(item)) return item;
      if (item.children) {
        const found = this.findItem(item.children, matcher);
        if (found) return found;
      }
    }
    return undefined;
  }
}
